import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useMatches, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { XStack } from 'tamagui';
import { GBAlert, GBBadge, GBSelect, GBSkeleton, GBText } from '@greenbackapp/ui';
import { useBusiness } from '../../contexts/BusinessContext';
import { BusinessAccountingProvider } from '../../contexts/BusinessAccountingContext';
import { BusinessModeProvider } from '../../hooks/useBusinessDataMode';
import { BusinessOnboarding } from './BusinessOnboarding';
import { PreviewBanner } from './common/PreviewBanner';
import { ModuleLockBanner } from './common/ModuleLockBanner';
import {
  BusinessUpsellBanner,
  PaymentDeclinedBanner,
  ReadOnlyBusinessBanner,
} from './common/EntitlementBanners';
import { subscribeBusinessReadOnly } from '../../services/businessReadOnlyNotifier';
import { subscribeBusinessPeriodLock } from '../../services/businessPeriodLockNotifier';
import { subscribeFirmRequired } from '../../services/businessFirmRequiredNotifier';
import { subscribeRoleForbidden } from '../../services/businessRoleForbiddenNotifier';
import { RoleForbiddenNotice } from './common/RoleForbiddenNotice';
import { useToast } from '../../contexts/ToastContext';
import { ReceiptCaptureButton } from '../banking/receipts/ReceiptCaptureButton';
// The one source for what a role is called. This file used to keep its own
// four-role copy, so an AR clerk's badge read "ar_clerk".
import { ROLE_LABELS } from './settings/roleCopy';
import { fieldRoleAllows, fieldRoleHomePath } from './fieldRoleLanding';
import { BUSINESS_BILLING_PATH, useGroupBillingTarget } from './common/pausedModules';
import { isInvoiceAlert } from '../../types/platformBilling';

// Shown whenever the API rejects a mutation with 403 business_read_only —
// the safety net for any affordance the central gate missed. Neutral on
// purpose: the refusal does not say why the books are read-only, and a group
// whose card subscription is only paused has not lapsed. The banner beside it
// says why, when this viewer has been told.
export const READ_ONLY_TOAST_MESSAGE = "This change wasn't saved — the business is read-only.";

const ShellHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 32px 0;

  @media (max-width: 767px) {
    padding: 12px 16px 0;
  }
`;

// 220px was a floor with no ceiling, and beside the role badge it was wider
// than a phone: every business page scrolled sideways because of this one
// control. On a phone it takes the row instead.
const SwitcherWrap = styled.div`
  min-width: 220px;

  @media (max-width: 767px) {
    flex: 1 1 100%;
    min-width: 0;
  }
`;

const BannerWrap = styled.div`
  margin: 16px 24px 0;
`;

// Route prefixes wired to the live accounting API.
//
// As of PRD 36 this is EVERY route under /business — inventory was the last
// sample-data screen, and the banner it kept is gone with it. The list stays
// (rather than collapsing to a single '/business' prefix) because it is what
// makes adding the next preview screen safe: a route not named here shows the
// banner by default, which is the right way round.
const LIVE_ROUTE_PREFIXES = [
  '/business/accounting',
  '/business/settings',
  '/business/banking',
  '/business/customers',
  '/business/vendors',
  '/business/invoices',
  '/business/bills',
  '/business/reports',
  '/business/credit-memos',
  '/business/vendor-credits',
  '/business/tax',
  '/business/inventory',
  '/business/service-items',
  // The fixed-asset register (PRD 52) is live, and it is reached at
  // '/business/accounting/fixed-assets' — already covered by the accounting
  // prefix above, so it needs no row of its own.
  //
  // THESE THREE WERE MISSING, and every screen under them has been drawing a
  // "sample data" banner over real, live records since it shipped. Properties
  // (web#172), leases (PRD 47) and maintenance (PRD 54) all read and write the
  // production API. Fixing it here rather than filing it: the trap this list's
  // own comment warns about had already caught three routes, and adding a
  // fourth beside them without saying so would have been worse.
  '/business/properties',
  '/business/leases',
  '/business/maintenance',
  // The CRM module (PRD 69). Live records, not sample data.
  '/business/crm',
  // Messages with your accountant (PRD 68 §5.2). Missed like the three above:
  // the banner told an owner their real conversation was sample data.
  // navigationRoutes.test.ts now fails on the next route that misses this list.
  '/business/messages',
  // Field service (PRD 88 onwards). Every later field page lives under this
  // prefix, so it needs no row of its own.
  '/business/field',
  // The cannabis module (PRD 111 §5): the profile, licenses and chart are
  // live records, joined in the PR that adds the routes.
  '/business/cannabis',
  // Point of sale (PRD 118): the set-up now, the register in W-b.
  '/business/pos',
  // Personal services (PRD 121 §5.2): Overview, Set-up and Settings now; the
  // calendar (PRD 122), staff, locations, menu and (PRD 122 Wave 5)
  // /business/appointments/standing join under this prefix — and PRD 126's
  // /business/appointments/checkout[/:appointmentId] and /takings, and
  // (W2) /business/appointments/tips and /my-tips.
  // '/business/staff' joins in Wave 2 with its first route.
  '/business/appointments',
  // PRD 125 §5: Sales → Cancellation fees — the live fee queue (core).
  '/business/cancellation-fees',
];

// The module index is live too, but it cannot join the list above: as a
// PREFIX, '/business' matches every route under it and would silence the
// banner on the screens that really are still sample data.
const LIVE_EXACT_ROUTES = [ '/business' ];

const SETTINGS_PATH = '/business/settings';

export const isLiveRoute = (pathname: string): boolean =>
  LIVE_EXACT_ROUTES.includes(pathname) ||
  LIVE_ROUTE_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`));

/** The shape of a `useMatches()` entry this file needs — enough for a test to
 *  hand it plain objects. */
interface RouteMatchLike {
  pathname: string;
  params: Record<string, string | undefined>;
}

/**
 * Where a business switch should land, or null to stay on the current route.
 *
 * A route carrying a path param names a ROW — invoice `:id`, lease `:leaseId`,
 * `:bankAccountId/register` — and that row belongs to the tenant being left.
 * Re-fetching it under the incoming business asks for an id that is not there,
 * so a switch that worked would answer with a 404. Cut the path at its first
 * parameterised segment and land on what is left: the section that owns the
 * row, which exists in every tenant.
 *
 * A route with no params means the same thing in either business, so it is left
 * alone — switching on the balance sheet should show the new business's balance
 * sheet, not send anyone back to the dashboard.
 *
 * Cutting the PATH rather than walking up to a param-free ancestor match is
 * deliberate: `useMatches()` gives every entry the fully accumulated params, so
 * the ancestors of a record route carry the leaf's params too and cannot be
 * told apart by their params at all.
 */
export const businessSwitchLandingPath = (matches: RouteMatchLike[]): string | null => {
  const deepest = matches[matches.length - 1];
  if (!deepest) return null;

  const values = new Set(
    Object.values(deepest.params).filter((value): value is string => Boolean(value))
  );
  if (values.size === 0) return null;

  const segments = deepest.pathname.split('/').filter(Boolean);
  // Decoded, because `params` are decoded and the pathname is not.
  const cut = segments.findIndex(segment => values.has(decodeURIComponent(segment)));
  // No segment matched: a splat param spanning several of them, or a param that
  // is not in the path at all. Staying put beats guessing.
  if (cut < 0) return null;

  const landing = `/${segments.slice(0, cut).join('/')}`;
  // Never land outside the section — cutting at the first segment would leave
  // '/', and dropping someone out of Business is worse than its index.
  return landing.startsWith('/business') ? landing : '/business';
};

/**
 * Live-mode layout for /business/*: loads the tenant list, then branches on
 * the SKU entitlement (IN_PLACE_ADAPTATION.md "Monetization"):
 *  - no businesses + a paid group → onboarding into the group they paid for
 *    before naming a business (PRD 86 §3)
 *  - no businesses + not entitled → teaser: the sample-data preview with an
 *    upsell banner (flag = deployed, SKU = entitled; the preview is the trial)
 *  - no businesses + entitled     → onboarding (create the first business)
 *  - businesses (own or invited)  → live mode with the business switcher;
 *    a read_only business additionally gets a persistent banner, worded from
 *    the billing alert: lapsed, paused, or read-only with no reason given.
 */
export const LiveBusinessShell: React.FC = () => {
  const {
    businesses,
    currentBusiness,
    hasLoaded,
    error,
    canCreateBusiness,
    paidEmptyGroupId,
    readOnly,
    selectBusiness,
    refresh,
    refreshPeriodLock,
  } = useBusiness();
  const location = useLocation();
  const navigate = useNavigate();
  // Null only while the list is empty, and the switcher does not exist then.
  const switchKey = currentBusiness?.id ?? null;
  const { showError } = useToast();
  const matches = useMatches();

  // Where an owner fixes this group's bill: Manage subscription, deep-linked
  // to the group and the task, once the list says they manage it; the billing
  // card before. The same rule as every paused module offer.
  const billingTarget = useGroupBillingTarget(currentBusiness?.entity_group_id);
  // Greenback's own invoice (PRD 112 Wave 2): its banners go to the billing
  // card, where the invoice is, never to a card task on Manage subscription.
  const invoiceAlert = isInvoiceAlert(currentBusiness?.billing_alert);

  // Read through a ref so the effect below depends on the BUSINESS only. The
  // matches array is new on every navigation, and taking it as a dependency
  // would re-run the switch handler on ordinary page-to-page movement.
  const matchesRef = useRef<RouteMatchLike[]>(matches);
  matchesRef.current = matches;

  // 403 role_forbidden (PRD 89 §4.9): which page a field role was refused, in
  // which business, holding which role. The page is drawn as one sentence
  // instead of whatever half of it loaded — never a toast per request.
  //
  // Keyed on all three rather than cleared on navigation. The PAGE is the one
  // that sent the request (businessRequestOrigin), so a refusal answering a
  // page already left marks that page and never the one now open; the
  // BUSINESS and ROLE are compared so that switching to a business where this
  // person is an owner, or being promoted, draws the page again.
  const [roleForbidden, setRoleForbidden] = useState<{
    path: string;
    businessId: string | null;
    role: string | null;
  } | null>(null);
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const currentRole = currentBusiness?.role ?? null;
  const roleRef = useRef<string | null>(currentRole);
  roleRef.current = currentRole;
  const businessIdRef = useRef<string | null>(switchKey);
  businessIdRef.current = switchKey;

  // A SWITCH HAS TO REACH THE SCREEN, not just the next request.
  //
  // Nothing under this shell subscribes to the active tenant: business pages
  // fetch once on mount and the id reaches them only as the ambient
  // X-Business-Id header (BusinessContext.applyBusinessId). So picking a
  // different business swapped the header and left the outgoing tenant's rows
  // sitting on screen until something happened to remount the page — which is
  // the bug. `switchKey` below remounts the routed subtree, re-running every
  // one of those mount fetches under the new header; it changes on a switch and
  // at no other time, so it costs nothing on an ordinary render.
  //
  // Discarding an in-progress form along with it is the point, not a
  // side-effect: a half-filled invoice submitted after a switch would post into
  // the business that was just selected.
  const previousBusinessId = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousBusinessId.current;
    previousBusinessId.current = switchKey;
    // First resolution is not a switch — there is nothing stale to leave.
    if (!previous || !switchKey || previous === switchKey) return;

    const landing = businessSwitchLandingPath(matchesRef.current);
    // PRD 89 §4.12: a field role in the incoming business lands on its own
    // page when the route it would be left on is one that role cannot use —
    // a technician switching in from the books is not met by a refusal.
    const role = roleRef.current;
    if (!fieldRoleAllows(role, landing ?? pathnameRef.current)) {
      navigate(fieldRoleHomePath(role) ?? '/business', { replace: true });
      return;
    }
    // `replace`: the row that was on screen belongs to the other business, so
    // leaving it in history only offers a Back button onto a 404.
    if (landing) navigate(landing, { replace: true });
  }, [switchKey, navigate]);

  // 403 business_read_only safety net: consistent toast + refresh the tenant
  // list so the flipped access_mode reaches the banner and mutability gate.
  useEffect(
    () =>
      subscribeBusinessReadOnly(() => {
        showError(READ_ONLY_TOAST_MESSAGE);
        void refresh();
      }),
    [showError, refresh]
  );

  // 403 firm_required: an accountant or bookkeeper with no practice cannot
  // open a client's books (PRD 40 §5.5.1, revised). No toast — a message that
  // says "you cannot do this" over a screen with nothing on it is worse than
  // the screen that lets them fix it, so this navigates instead.
  //
  // `required` tells activation it was reached through the gate rather than
  // through the optional offer, which changes what it may say and whether it
  // can be left. `from` is where to put them back afterwards.
  useEffect(
    () =>
      subscribeFirmRequired(() => {
        navigate(
          `/firm/activate?required=1&from=${encodeURIComponent(location.pathname)}`,
          { replace: true }
        );
      }),
    [navigate, location.pathname]
  );

  useEffect(
    () =>
      subscribeRoleForbidden(notice => {
        setRoleForbidden({
          // Unknown origin (a response the fetch layer did not see) means the
          // page that is open: the best answer there is.
          path: notice.path ?? pathnameRef.current,
          businessId: notice.businessId ?? businessIdRef.current,
          role: roleRef.current,
        });
      }),
    []
  );

  const showRoleForbidden =
    roleForbidden !== null &&
    roleForbidden.path === location.pathname &&
    roleForbidden.businessId === switchKey &&
    roleForbidden.role === currentRole;

  // 422 period_locked safety net: the same shape as the read-only one — one
  // throttled toast, plus a re-read of the close status so a gate that was
  // out of date catches up (PRD 08 §8.5).
  useEffect(
    () =>
      subscribeBusinessPeriodLock(notice => {
        showError(notice.message);
        void refreshPeriodLock();
      }),
    [showError, refreshPeriodLock]
  );

  if (!hasLoaded) {
    return (
      <BannerWrap>
        <GBSkeleton type="card" height={160} />
      </BannerWrap>
    );
  }

  if (error && businesses.length === 0) {
    return (
      <BannerWrap>
        <GBAlert
          variant="error"
          title="Could not load your businesses"
          message={error}
          action={{ label: 'Retry', onPress: () => void refresh() }}
        />
      </BannerWrap>
    );
  }

  if (businesses.length === 0) {
    // Settings is the one page that must survive an empty business list: it
    // hosts the pending-invitation inbox, and someone whose only relationship
    // to Greenback Business is an invitation would otherwise have no way to
    // reach the screen that lets them accept it (PRD 28 §10.1).
    // PREFIX match, not equality. Administration is a hub with sub-pages now
    // (PRD 29 §8.3); an exact match would leave /business/settings/profile and
    // friends falling through to the upsell branch below, which is where an
    // invitee with no businesses would land after one click.
    if (location.pathname === SETTINGS_PATH
        || location.pathname.startsWith(`${SETTINGS_PATH}/`)) {
      return (
        <BusinessAccountingProvider>
          <Outlet />
        </BusinessAccountingProvider>
      );
    }

    // Three cases remain, in this order.
    //
    // Paid for, not yet named (PRD 86 §3): they bought Greenback Business
    // before any business existed, so the business is created into the group
    // they paid for. Ahead of the other two: the upsell would sell them what
    // they already bought, and a business on its own would land outside the
    // group their subscription is on.
    if (paidEmptyGroupId) return <BusinessOnboarding entityGroupId={paidEmptyGroupId} />;

    // Unentitled with no memberships: keep the dummy-data preview as the
    // teaser, swapping the plain preview banner for the upsell. Members of
    // someone else's business (non-empty list) never land here — they get
    // live mode via their membership regardless of their own subscription.
    if (!canCreateBusiness) {
      return (
        <BusinessModeProvider value="preview">
          <BusinessAccountingProvider>
            <BusinessUpsellBanner />
            <Outlet />
          </BusinessAccountingProvider>
        </BusinessModeProvider>
      );
    }

    // Entitled on a rail of their own: a business on its own.
    return <BusinessOnboarding />;
  }

  return (
    <BusinessAccountingProvider>
      <ShellHeader>
        {/* PRD 21 §6.1: on every page, and outside the keyed page, so a receipt
            still uploading carries on while the person moves between pages. */}
        <ReceiptCaptureButton />
        {currentBusiness && (
          <XStack gap={8} alignItems="center">
            <GBBadge variant="neutral" size="sm">
              {ROLE_LABELS[currentBusiness.role] ?? currentBusiness.role}
            </GBBadge>
            <GBText variant="bodySmall" color="secondary">
              Business
            </GBText>
          </XStack>
        )}
        <SwitcherWrap>
          <GBSelect
            value={currentBusiness?.id}
            onChange={selectBusiness}
            options={businesses.map(business => ({ value: business.id, label: business.name }))}
            placeholder="Select business"
          />
        </SwitcherWrap>
      </ShellHeader>
      {/* One banner for read-only, never two. The banner reads the alert this
          viewer was sent: a paused card subscription gets the paused banner,
          because "lapsed / Resubscribe" would be false, and a member nobody
          told why is told the books are read-only and no more. Only inside
          `readOnly`: the paused banner says the books are read-only, so it
          must never show beside writable ones. */}
      {readOnly && currentBusiness && (
        <ReadOnlyBusinessBanner
          isOwner={currentBusiness.role === 'owner'}
          alert={currentBusiness.billing_alert ?? null}
          onManageBilling={() =>
            navigate(invoiceAlert
              ? BUSINESS_BILLING_PATH
              : billingTarget(currentBusiness.billing_alert?.status === 'paused' ? 'resume' : null))}
        />
      )}
      {/* A failed payment, while they can still work. Null unless this viewer
          should be told — the server decides that (Billing::AlertVisibility),
          so a firm's declined card never reaches its clients. */}
      {currentBusiness?.billing_alert && (
        <PaymentDeclinedBanner
          businessId={currentBusiness.id}
          alert={currentBusiness.billing_alert}
          onFix={() => navigate(invoiceAlert ? BUSINESS_BILLING_PATH : billingTarget('card'))}
        />
      )}
      {!isLiveRoute(location.pathname) && <PreviewBanner />}
      {/* Property management or inventory, enforced by the api and not held.
          Draws nothing until the api enforces one (N3). */}
      {!showRoleForbidden && <ModuleLockBanner pathname={location.pathname} />}
      {showRoleForbidden ? (
        <RoleForbiddenNotice
          role={currentRole}
          businessName={currentBusiness?.name}
          currentPath={location.pathname}
          onNavigate={path => navigate(path)}
        />
      ) : (
        /* Keyed so a business switch remounts the page — see switchKey above. */
        <Outlet key={switchKey ?? 'no-business'} />
      )}
    </BusinessAccountingProvider>
  );
};

export default LiveBusinessShell;
